<template>
    <section
        class="mt-2 flex w-full max-w-[780px] flex-col overflow-hidden rounded-[10px] border border-gray-300 bg-white"
        :data-testid="`${testidPrefix}-feedback-panel`"
    >
        <div
            class="flex items-center justify-between border-b border-gray-300 bg-gray-150 px-4 py-2"
        >
            <p class="my-0 text-sm font-normal leading-5 text-gray-800">
                {{ $t(`${i18nNamespace}.feedbackTitle`) }}
            </p>
            <button
                class="inline-flex size-5 items-center justify-center text-gray-700 hover:text-gray-800"
                :aria-label="$t('general.close')"
                :data-testid="`${testidPrefix}-feedback-panel-close`"
                type="button"
                @click="$emit('close')"
            >
                <icon height="16px" name="bold/close" width="16px" />
            </button>
        </div>

        <div class="flex flex-col gap-3 px-4 py-3">
            <div class="flex flex-wrap items-center gap-[7px]">
                <button
                    class="inline-flex h-8 items-center gap-[11px] rounded border px-2 py-1.5 text-sm"
                    :aria-pressed="isReasonSelected(reason)"
                    :class="
                        isReasonSelected(reason)
                            ? 'border-blue-550 bg-blue-100 font-medium text-blue-500'
                            : 'border-gray-300 bg-white font-normal text-gray-700'
                    "
                    :data-testid="`${testidPrefix}-feedback-reason-${reason}`"
                    type="button"
                    :key="reason"
                    @click="toggleReason(reason)"
                    v-for="reason in feedbackReasonKeys"
                >
                    <span
                        class="inline-flex size-[18px] items-center justify-center rounded-sm border shadow-sm"
                        :class="
                            isReasonSelected(reason)
                                ? 'border-blue-550 bg-blue-550'
                                : 'border-gray-450 bg-white'
                        "
                    >
                        <icon
                            class="text-white"
                            :data-testid="`${testidPrefix}-feedback-reason-check`"
                            height="12px"
                            name="regular/check-1"
                            width="12px"
                            v-if="isReasonSelected(reason)"
                        />
                    </span>
                    <span>{{ $t(`${i18nNamespace}.feedbackReasons.${reason}`) }}</span>
                </button>
            </div>

            <textarea
                class="h-[50px] w-full resize-none rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 placeholder:text-gray-550 focus:border-blue-550 focus:outline-none"
                :data-testid="`${testidPrefix}-feedback-details`"
                :maxlength="feedbackDetailsMaxLength"
                :placeholder="$t(`${i18nNamespace}.feedbackDetailsPlaceholder`)"
                v-model="details"
            />
        </div>

        <div class="flex justify-end px-4 pb-2.5">
            <button
                class="rounded-sm border border-blue-700 bg-blue-600 px-3 py-[5px] text-sm text-white hover:bg-blue-700"
                :data-testid="`${testidPrefix}-feedback-panel-submit`"
                type="button"
                @click="submitFeedback"
            >
                {{ $t(`${i18nNamespace}.feedbackSubmit`) }}
            </button>
        </div>
    </section>
</template>

<script setup lang="ts">
    import { ref } from 'vue';
    import type { AiDraftFeedbackSubmission } from '@/components/aiDraft/feedback/types.ts';
    import {
        DRAFT_FEEDBACK_REASON_KEYS,
        DraftFeedbackReason,
    } from '@/entities/enums/DraftFeedbackReason.ts';

    defineProps<{
        testidPrefix: string;
        i18nNamespace: string;
    }>();

    const feedbackReasonKeys = DRAFT_FEEDBACK_REASON_KEYS;

    const emit = defineEmits<{
        close: [];
        submit: [submission: AiDraftFeedbackSubmission];
    }>();

    const details = ref('');
    const feedbackDetailsMaxLength = 1000;
    const selectedReasons = ref<DraftFeedbackReason[]>([]);

    const isReasonSelected = (reason: DraftFeedbackReason) =>
        selectedReasons.value.includes(reason);

    const toggleReason = (reason: DraftFeedbackReason) => {
        selectedReasons.value = isReasonSelected(reason)
            ? selectedReasons.value.filter((selectedReason) => selectedReason !== reason)
            : [...selectedReasons.value, reason];
    };

    const submitFeedback = () => {
        const trimmedDetails = details.value.trim();

        emit('submit', {
            reasons: [...selectedReasons.value],
            details: trimmedDetails.length > 0 ? trimmedDetails : null,
        });
    };
</script>
